/* eslint-disable object-curly-newline */
import { defineStore } from "pinia";
import { toRaw } from 'vue';
import { sDs } from '@common/sDs/sDs';
import { library } from "@common/library/library";
import { configStore } from "./configStore";

// The actual object where data is stored
// The pinia store will return a proxy of this object
// The proxy creates sub-records and sub-fields if they are not available in the store
// The proxy may be configured to request data from the server if the field does not exist (not currently enabled)
const proxyKeys = {
    lbeIds: 'relKeys',
    affIds: 'relKeys',
    assignmentIds: 'relKeys',
    placedIds: 'relKeys',
    referredIds: 'relKeys',
    historyIds: 'relKeys',
    schIds: 'relKeys',
    affiliations: 'rel',
    assignments: 'rel',
    placements: 'rel',
    referrals: 'rel',
    lbes: 'rel',
    shifts: 'rel',
    vol: 'psp',
    opp: 'psp',
    org: 'psp',
    sch: 'psp',
    acc: 'psp',
    mem: 'psp',
    lbe: 'psp',
    aff: 'psp',
    assignment: 'psp',
    ae: 'psp',
    history: 'psp',
    cstProperty: 'psp',
    subscription: 'psp',
    uiTagged: 'psp',
    aaw: 'psp',
    userRole: 'psp',
    linked_opp: 'link',
    linked_vol: 'link',
    linked_org: 'link',
    // inSubscription: 'internal',
    uiRecordPsp: 'internal',
    uiRecordId: 'internal',
    // uiRecordKey: 'internal',
    records: 'internal',
    __v_isRef: 'ignore',
    __v_raw: 'ignore',
    __v_isReadonly: 'ignore',
    __v_skip: 'ignore',
    length: 'ignore',
    strict: 'strict'
};

const keyType = (key) => {
    if (typeof key === 'symbol') return 'ignore';
    if (proxyKeys[key]) return proxyKeys[key];
    // undefined keys are either a record ID a field or a subscription
    if (!isNaN(key)) return 'id';
    return 'field';
};

let pspKeys = {};
for (let key in proxyKeys) {
    if (keyType(key) === 'psp') pspKeys[key] = {};
}
const handlerStore = library.cloneObj(pspKeys);
handlerStore.strict = library.cloneObj(pspKeys);
let currentHandlerStore = library.cloneObj(handlerStore); // Reuse this variable when resetting

// catch all nested properties and returns the proxy e.g. this.recordStore.vol || this.recordStore.vol[123] || this.recordStore.vol[123].VOL_FIRST_NAME
const isProxy = Symbol('isProxy'); // Unique key that cannot be overwritten without using this variable

let recordProxy = { strict: {} }; // create individual proxy for IDs and Fields only (create the perspectives before proxy)
let autosubscribe = library.cloneObj(pspKeys); // for disabling autosubscribe
let queuedSubscriptions = library.cloneObj(pspKeys); // for queuing subscriptions while we are still iterating through DOM arrays of records
let inSubscription = library.cloneObj(pspKeys); // for tracking subscriptions

const recordHandler = {
    get(obj, key, receiver) { // vol.VOL_FIRST_NAME
        let psp = obj.perspective;
        let kType = keyType(key);
        if (kType === 'ignore') return Reflect.get(...arguments); // Don't create proxies on symbols
        if ((kType === 'psp' || kType === 'id' || kType === 'rel' || kType === 'link' || kType === 'strict') && !obj.isRelId && (!obj[key] || !obj[key][isProxy])) {
            if (!obj[key]) obj[key] = {};
            // set the perspective for access in other handler conditions
            // let perspective = kType === 'psp' ? key : psp;
            let isStrict = kType === 'strict' ? 1 : obj.isStrict;
            Object.defineProperty(obj[key], 'perspective', {
                enumerable: false,
                value: psp
            });
            Object.defineProperty(obj[key], 'isStrict', {
                enumerable: false,
                value: isStrict
            });
            if (kType === 'id' && psp !== 'subscription' && psp !== 'uiTagged') {
                obj[key].uiRecordId = Number(key);
                obj[key].uiRecordPsp = psp;
            }
            obj[key][isProxy] = true;
            obj[key] = new Proxy(obj[key], recordHandler);
        }
        // if (psp === 'subscription') {
        //     if (key === 'rsLastModified') {
        //         let objRecords = {}; // Make changes to this var instead of directly to obj to prevent flickering
        //         let subscription = currentHandlerStore.subscriptions[obj.subscriptionKey];
        //         if (subscription && subscription.sourceType === 'uiTag') {
        //             if (currentHandlerStore.records.uiTagged[subscription.params.tagName]) {
        //                 obj.records = currentHandlerStore.records.uiTagged[subscription.params.tagName].records;
        //                 obj.rowCount = currentHandlerStore.records.uiTagged[subscription.params.tagName].rowCount;
        //                 // rsLastModified for subscribers of this tagged data is set in getSubscription getter
        //             }
        //         } else if (subscription && subscription.psp === 'cstProperty') {
        //             // TODO figure out a better way to make sure this object/subkey exists
        //             let subscription = currentHandlerStore.subscriptions[obj.subscriptionKey];
        //             if (subscription.params && subscription.params.cstProperty) {
        //                 if (currentHandlerStore.records.cstProperty[subscription.params.cstProperty]
        //                     && currentHandlerStore.records.cstProperty[subscription.params.cstProperty][subscription.params.subKey]) {
        //                     obj.records = currentHandlerStore.records.cstProperty[subscription.params.cstProperty][subscription.params.subKey];
        //                 } else {
        //                     obj.records = {};
        //                 }
        //             }
        //         } else {
        //             if (subscription && subscription.storeOnlyReturnedRecords) obj.records = [];
        //             let subIdsLen = subscription && subscription.recordIds && subscription.recordIds.length;
        //             if (subIdsLen) {
        //                 for (let i = 0; i < subIdsLen; i++) {
        //                     let recordId = subscription.recordIds[i];
        //                     if (recordId || (subscription.idAsKey && recordId >= 0)) {
        //                         if (!currentHandlerStore.records[subscription.psp][recordId]?.inSubscription) {
        //                             currentHandlerStore.records[subscription.psp][recordId].inSubscription = {};
        //                         }
        //                         currentHandlerStore.records[subscription.psp][recordId].inSubscription[obj.subscriptionKey] = true;
        //                         objRecords[recordId] = currentHandlerStore.records[subscription.psp][recordId];
        //                     }
        //                 }
        //             }
        //             obj.records = objRecords;
        //         }
        //     }
        //     else if (kType === 'id') {
        //         if (!obj[key].rsLastModified) obj[key].rsLastModified = false;
        //         if (!obj[key].records) obj[key].records = {};
        //         Object.defineProperty(obj[key], 'subscriptionKey', {
        //             enumerable: false,
        //             value: key
        //         });
        //         let subscription = currentHandlerStore.subscriptions[key];
        //         if (subscription && subscription.source === 'uiTag'
        //             && subscription.payload
        //             && subscription.payload.params
        //             && subscription.payload.params.tagName
        //         ) {
        //             Object.defineProperty(obj[key], 'uiTag', {
        //                 enumerable: false,
        //                 value: subscription.payload.params.tagName
        //             });
        //         }
        //     }
        // }
        // process relKeys properties
        if (kType === 'relKeys' && (!obj[key] || !obj[key][isProxy])) {
            if (!obj[key]) obj[key] = {};
            // use isRelId for the next level up
            Object.defineProperty(obj[key], 'isRelId', {
                enumerable: false,
                value: true
            });
            obj[key][isProxy] = true;
            obj[key] = new Proxy(obj[key], recordHandler);
        }
        // set relative keys to false by default
        if (obj.isRelId && typeof obj[key] === 'undefined') obj[key] = false;
        // Convert lbeIds to lbes (keys to records)
        if (kType === 'rel' && obj.uiRecordId) {
            let convertedKeys = {
                lbes: { relPsp: 'lbe', idsKey: 'lbeIds' },
                affiliations: { relPsp: 'aff', idsKey: 'affIds' },
                shifts: { relPsp: 'sch', idsKey: 'schIds' },
                referrals: { relPsp: 'referrals', idsKey: 'referredIds' },
                placements: { relPsp: 'placements', idsKey: 'placedIds' }
            };
            let relPsp = convertedKeys[key].relPsp;
            let idsKey = convertedKeys[key].idsKey; // lbeIds, schIds, affIds
            let recordKeys = [];
            let relIds = currentHandlerStore[psp][obj.uiRecordId][idsKey];
            if (relIds) recordKeys = Object.keys(relIds);
            let relRecords = {};
            recordKeys.forEach((iKey) => {
                relRecords[iKey] = currentHandlerStore[relPsp][iKey];
            });
            obj[key] = relRecords;
        }
        if (kType === 'link') {
            let linkPsp = key.split('_')[1];
            let linkedRecordId = obj[key].uiRecordId;
            let record = obj[key]; // default
            if (linkedRecordId) record = currentHandlerStore[linkPsp][linkedRecordId]; // relative
            // remove all relative records in linked records to prevent circular dependency
            let { lbeIds, affIds, schIds, lbes, affiliations, shifts, ...otherKeys } = record;
            obj[key] = otherKeys;
        }
        let tmpValue = obj[key];
        // autosubscriber
        if (kType === 'field' && key !== 'uiRecordId' && key !== 'toJSON' && key !== 'ID' && key !== 'uiDelete') {
            // if Field and no subscription for the record, and the value is undefined, subscribe to get the field
            // let recordId = receiver.uiRecordId;
            let recordId = obj.uiRecordId;
            if (recordId && typeof obj[key] === 'undefined' && autosubscribe?.[psp]?.[recordId] !== false) {
                autosubscribe[psp][recordId] = false;
                // eslint-disable-next-line no-use-before-define
                let rStore = typeof recordStore === 'function' && recordStore();
                // let fields = [key];
                obj[key] = null;
                if (queuedSubscriptions[psp].timeout) clearTimeout(queuedSubscriptions[psp].timeout);
                if (!queuedSubscriptions[psp].recordIds) queuedSubscriptions[psp].recordIds = [];
                if (!queuedSubscriptions[psp].fields) queuedSubscriptions[psp].fields = [];
                queuedSubscriptions[psp].recordIds.push(recordId);
                queuedSubscriptions[psp].fields.push(key); // add the fields (even duplicated) and then make a unique set in the timeout
                queuedSubscriptions[psp].timeout = setTimeout(() => {
                    let recordIds = queuedSubscriptions[psp].recordIds;
                    let fields = new Set(queuedSubscriptions[psp].fields);
                    fields = [...fields];
                    if (rStore) {
                        let source = 'er_' + psp + 'Fields';
                        if (source === 'er_affFields') source = 'sds_getProfileField';
                        rStore.subscribe({ source: [source], params: { recordId: recordIds, fields, recordPsp: psp } }, 'autosubscriber');
                    }
                    queuedSubscriptions[psp] = {}; // Reset the queue
                }, 500);
            }
            if (!obj.isStrict) {
                // isStrict = return data as returned from server, without flag parse data from server
                // @todo isStrict should be equal with strictTypeResults and recordStore should keep both variants of data
                if (typeof tmpValue === 'string' && tmpValue.indexOf('datePeriod') !== -1 && library.isJson(tmpValue)) {
                    let dateJson = JSON.parse(tmpValue);
                    tmpValue = library.getDateBaseOnPeriod(dateJson.datePeriod, dateJson.amount, false, dateJson.unit, dateJson.day);
                }
            }
        }
        return tmpValue; // bob
    },
    // set(obj, key, value) {
    //     if (key === 'rsLastModified') {
    //         let subscription = currentHandlerStore.subscriptions[obj.subscriptionKey];
    //         let subTag = subscription && subscription.tagName;
    //         if (subTag) {
    //             if (value === -1) {
    //                 delete currentHandlerStore.records.uiTagged[subTag];
    //             } else {
    //                 let subIdsLen = subscription && subscription.recordIds && subscription.recordIds.length;
    //                 let rowCount = 0;
    //                 let uiTaggedRecords = {};
    //                 // Process tagLogic when records change
    //                 // Note: tagLogic is also processed when store properties change, handled in the getSubscription getter
    //                 for (let i = 0; i < subIdsLen; i++) {
    //                     let recordId = subscription.recordIds[i];
    //                     if (library.logicParser.call(subscription.tagParent, subscription.tagLogic, obj.records[recordId])) {
    //                         uiTaggedRecords[recordId] = obj.records[recordId];
    //                         rowCount++;
    //                     }
    //                 }
    //                 // handle tagging here
    //                 if (!currentHandlerStore.records.uiTagged[subTag]) {
    //                     currentHandlerStore.records.uiTagged[subTag] = {};
    //                 }
    //                 currentHandlerStore.records.uiTagged[subTag].records = uiTaggedRecords;
    //                 currentHandlerStore.records.uiTagged[subTag].rsLastModified = value;
    //                 currentHandlerStore.records.uiTagged[subTag].rowCount = rowCount;
    //             }
    //         }
    //     }
    //     obj[key] = value;
    //     return true;
    // }
};

const locals = {
    timeSinceLastRefresh: 0,
    subscriptionCheckFrequency: 10,
    setNonEnumKeys(currObj) {
        for (let pKey in currObj) {
            if (pKey === 'strict') continue;
            Object.defineProperty(currObj[pKey], 'perspective', {
                enumerable: false,
                value: pKey
            });
        }
        for (let pKey in currObj.strict) {
            Object.defineProperty(currObj.strict[pKey], 'perspective', {
                enumerable: false,
                value: pKey
            });
            Object.defineProperty(currObj.strict[pKey], 'isStrict', {
                enumerable: false,
                value: true
            });
        }
    },
    updatePerformance(subscription, perfKey, thisPerf) {
        // all times in milliseconds
        let subPerf = subscription.performance;
        let bestPerf = subPerf[perfKey].best;
        let worstPerf = subPerf[perfKey].worst;
        if (!bestPerf || thisPerf < bestPerf) subPerf[perfKey].best = thisPerf;
        if (!worstPerf || thisPerf > worstPerf) subPerf[perfKey].worst = thisPerf;
        subPerf[perfKey].history.push(thisPerf);
    },
    setupStores() {
        autosubscribe = library.cloneObj(pspKeys);
        inSubscription = library.cloneObj(pspKeys);
        currentHandlerStore = library.cloneObj(handlerStore);
        locals.setNonEnumKeys(currentHandlerStore);
        recordProxy = { strict: {} };
        for (let pspKey in currentHandlerStore) {
            if (pspKey === 'strict') continue;
            recordProxy[pspKey] = new Proxy(currentHandlerStore[pspKey], recordHandler);
            recordProxy.strict[pspKey] = new Proxy(currentHandlerStore.strict[pspKey], recordHandler);
        }
    }
};

locals.setupStores();

const recordStore = defineStore('recordStore', {
    state: () => ({
        records: recordProxy,
        requests: {},
        subscriptions: {},
        subscriptionSummaries: {}, // set these separately to reduce reactive updates
        pollingInterval: false, // seconds
        refreshInterval: false,
        excludeActionsFromFreezeDetection: ['startSubscriberPolling']
    }),
    getters: {
        getInSubscription() {
            return () => inSubscription;
        },
        getSubscription(state) {
            return (subscription) => {
                let subscriptionId = typeof subscription === 'object'
                    ? subscription.key
                    : subscription;
                subscription = state.subscriptions[subscriptionId];
                // checking changes to rsLastModified triggers updates to the subscription
                // let rsLastModified = state.rsLastModified[subscriptionId];
                // if (!rsLastModified) return {}; // empty records
                // Process logic that looks at layout stores here
                if (subscription.tagName) {
                    if (Array.isArray(subscription.payload.tagLogic)) {
                        subscription.payload.tagLogic.forEach((logicItem) => {
                            let rules = logicItem.criteria && logicItem.criteria.rules;
                            let hasStorePropRule = false;
                            if (Array.isArray(logicItem.criteria.rules)) {
                                rules.forEach((rule) => {
                                    if (rule.field.indexOf('Store Prop:') === 0 || (typeof rule.value === 'string' && rule.value.indexOf('Store Prop:') !== -1)) {
                                        // TODO store anc compare existing/new values ?
                                        hasStorePropRule = true;
                                    }
                                });
                            }
                            // TODO test and fix this without using rsLastModified
                            // if (hasStorePropRule) {
                            //     rsLastModified = new Date().getTime();
                            //     this.records.subscription[subscriptionId].rsLastModified = rsLastModified;
                            // }
                        });
                    }
                    // TODO test and fix this
                    // if this subscription is a uiTag subscription, Update all subscribers of the uiTagged data
                    // for (let sKey in this.records.subscription) {
                    //     if (sKey === 'length') continue;
                    //     if (this.records.subscription[sKey].uiTag === subscription.tagName) {
                    //         this.records.subscription[sKey].rsLastModified = rsLastModified;
                    //     }
                    // }
                }
                let recordIds = subscription.recordIds;
                let records = {};
                if (!Array.isArray(recordIds)) return records;
                let psp = subscription.psp;
                let subKey = subscription?.params?.subKey;
                let cstProperty = subscription?.params?.cstProperty;
                if (cstProperty) {
                    if (subKey) {
                        recordIds.forEach((rId) => {
                            records[rId] = state.records[psp][cstProperty][subKey][rId];
                        });
                    }
                    else {
                        recordIds.forEach((rId) => {
                            records[rId] = state.records[psp][cstProperty][rId];
                        });
                    }
                }
                else {
                    recordIds.forEach((rId) => {
                        records[rId] = state.records[psp][rId];
                    });
                }
                return records; // TODO add other data like summary info here
            };
        }
    },
    actions: {
        reset() {
            locals.setupStores();
            // this.records = recordProxy;
            this.records = {};
            setTimeout(() => {
                this.records = recordProxy;
            }, 1000);
            this.requests = {};
            this.subscriptions = {};
            locals.subscriptionCheckFrequency = 10;
            locals.timeSinceLastRefresh = 0;
            if (this.pollingInterval) clearInterval(this.pollingInterval);
            if (this.refreshInterval) clearInterval(this.refreshInterval);
            this.pollingInterval = false;
            this.refreshInterval = false;
        },
        // Start the subscription watch interval
        startSubscriberPolling() {
            if (!this.refreshInterval) {
                this.refreshInterval = setInterval(() => {
                    locals.timeSinceLastRefresh++; // Stored outside of the store to reduce reactivity
                }, 1000); // NOSONAR
            }
            if (!this.pollingInterval) {
                this.pollingInterval = setInterval(() => {
                    // Evaluates the request's updateFrequency and lastRequestTime time to determine if a request should be sent
                    // uncomment below to view store data
                    // DO NOT LEAVE THESE LOGS ACTIVE IN COMMITTED CODE
                    // console.log(this.subscriptions); /* @phpstan-ignore-line */
                    // console.log(this.records);       /* @phpstan-ignore-line */
                    this.renewSubscriptions();
                }, locals.subscriptionCheckFrequency * 1000); // NOSONAR
            }
        },
        findExistingSubscription(payload, existingKey, subscriber) {
            // Find using key
            if (existingKey && this.subscriptions[existingKey]) {
                if (subscriber) this.subscriptions[existingKey].subscribers[subscriber] = 'subscribed';
                return this.subscriptions[existingKey];
            }
            // Find using payload or single existing recordId
            let existingSub = false;
            // Normalize the params
            let params = payload?.params;
            let recordIds = params?.recordId || params?.volId;
            if (!recordIds) recordIds = [];
            else if (!Array.isArray(recordIds)) recordIds = [recordIds];
            let fields = payload?.params?.fields && library.cloneObj(payload.params.fields);
            if (!fields) fields = [];
            else if (!Array.isArray(fields)) fields = [fields];
            let psp = payload?.params?.recordPsp;
            let source = Array.isArray(payload.source) ? payload.source[0] : payload.source;
            let subsKey = Object.keys(this.subscriptions);
            // Process in reverse because most likely we are adding to most recent subscription
            for (let i = subsKey.length - 1; i >= 0; i--) {
                let iSub = this.subscriptions[subsKey[i]];
                let iPayload = iSub.payload;
                let iParams = iPayload?.params;
                let iSource = Array.isArray(iPayload.source) ? iPayload.source[0] : iPayload.source;
                // Find existing subscription with single recordId (fields request only eg: er_volFields)
                // and update the list of fields for the subscription
                // Normalize the params
                let recordIdsObj = iSub.recordIdsObj || {}; // Store the recordIds as an object for quicker reference
                let fieldKeysObj = iSub.fieldKeysObj || {}; // Store the recordIds as an object for quicker reference
                let iFields = iParams?.fields && library.cloneObj(iParams.fields);
                if (!iFields) iFields = [];
                else if (!Array.isArray(iFields)) iFields = [];
                let iPsp = iParams?.recordPsp || iParams?.psp;
                let samePsp = iPsp === psp;
                let sameSource = iSource === source;
                let sameRecords = samePsp && sameSource && recordIds.every((recordId) => recordIdsObj[recordId]);
                let sameFields = samePsp && sameSource && fields.every((fieldKey) => fieldKeysObj[fieldKey]);
                let sameStrictType = iParams?.params?.strictTypeResults === params?.params?.strictTypeResults;
                // let tmpRecordIds = Array.isArray(recordId) ? recordId : [recordId];
                // if (samePsp) { // Ignore if not same psp for performance
                //     for (let rIdx = 0; rIdx < recordIds.length; rIdx++) {
                //         sameRecordId = iRecordIds.indexOf(recordIds[rIdx]) !== -1;
                //         if (sameRecordId) break;
                //     }
                // }
                // if (payload.doNotAddRecords) {
                //     allowedAddRecord = [];
                //     if (sameRecordId && Array.isArray(iRecordIds) && iRecordIds.length > 1) sameRecordId = false;
                // }
                // prevent add records to the subscription if strictTypeResults is different
                let allowedAddRecord = (sameStrictType && !payload.doNotAddRecords)
                    ? ['er_volFields', 'er_oppFields', 'er_orgFields', 'er_memFields', 'er_slotFields', 'er_schFields', 'er_userFields', 'er_accFields', 'er_affFields', 'sds_getProfileField']
                    : [];
                if (samePsp && sameStrictType && sameSource && recordIds.length && fields.length && (sameRecords || allowedAddRecord.indexOf(source) !== -1)) {
                    iSub.existing = true;
                    // let recordAdded = false;
                    // if ((!fields || !iFields) && fields !== iFields) {
                    //     iFields = fields;
                    //     fieldsAdded = true;
                    // }
                    // else if (iFields) {
                    //     fields.forEach((field) => {
                    //         if (iFields.indexOf(field) === -1) { // NOSONAR
                    //             iFields.push(field);
                    //             fieldsAdded = true;
                    //         }
                    //     });
                    // }
                    // if (!Array.isArray(iRecordId)) iRecordId = [iRecordId];
                    // let tmpRecordId = Array.isArray(recordId) ? recordId[0] : recordId;
                    // if (iRecordIds.indexOf(tmpRecordId) === -1) {
                    //     recordAdded = true;
                    //     iRecordIds.push(tmpRecordId);
                    // }
                    // Put data into matching subscription
                    let newKeysObj;
                    if (!sameRecords) {
                        let iRecordIds = iParams && iParams.recordId;
                        if (iRecordIds && !Array.isArray(iRecordIds)) iRecordIds = [iRecordIds];
                        let recordSet = new Set([...iRecordIds, ...recordIds]);
                        iParams.recordId = [...recordSet];
                        newKeysObj = {};
                        for (let keyId in iParams.recordId) {
                            newKeysObj[iParams.recordId[keyId]] = true;
                        } // Store the recordIds as an object for quicker reference
                        iSub.recordIdsObj = newKeysObj;
                    }
                    if (!sameFields) {
                        if (sameRecords) { // Add new fields as a special one-time request for just the new fields
                            iSub.recordFields = {
                                fields: library.arrayDifference(iFields, fields),
                                recordId: recordIds
                            };
                        }
                        let fieldSet = new Set([...iFields, ...fields]);
                        iParams.fields = [...fieldSet];
                        newKeysObj = {};
                        for (let keyId in iParams.fields) {
                            newKeysObj[iParams.fields[keyId]] = true;
                        } // Store the fieldKeys as an object for quicker reference
                        iSub.fieldKeysObj = newKeysObj;
                    }
                    if (!sameFields || !sameRecords) iSub.existing = false;
                    // if (allowedAddRecord.indexOf(source) !== -1) {
                    //     // collect fields by record
                    //     if (typeof iSub.recordFields === 'undefined') iSub.recordFields = {};
                    //     // let tmpRecordIds = Array.isArray(recordId) ? recordId : [recordId]; // Sometimes recordId is an array
                    //     let perf1 = performance.now();
                    //     // for (let ii in tmpRecordIds) {
                    //     //     if (ii === 'length') continue;
                    //     //     let tmpId = tmpRecordIds[ii];
                    //     //     if (!Array.isArray(iSub.recordFields[tmpId])) iSub.recordFields[tmpId] = [];
                    //     //     for (let i in iFields) {
                    //     //         if (iSub.recordFields[tmpId].indexOf(iFields[i]) === -1) iSub.recordFields[tmpId].push(iFields[i]);
                    //     //     }
                    //     //     iSub.recordFields[tmpId].sort();
                    //     // }
                    // }
                    existingSub = iSub;
                    break;
                }
                // Find existing request by payload
                else if (iPayload && library.isEqualObj(payload, iPayload)) {
                    iSub.existing = true;
                    existingSub = iSub;
                    break;
                }
            }
            if (existingSub && subscriber) existingSub.subscribers[subscriber] = 'subscribed';
            return existingSub;
        },
        getSubscriptionDetails(payload, existingKey, subscriber, tagParent) {
            // Find existing subscription by comparing source and params to existing
            // Returns existing or new subscription
            let existingSub = this.findExistingSubscription(payload, existingKey, subscriber);
            // Put the new request in the subscriptions for replaying the request on interval
            if (!existingSub) {
                // Define Variables
                let { source, params, parentRecords, addParams, tagName, tagLogic, updateFrequency, sourceLogic, sDsParams } = payload;
                if (Array.isArray(source)) source = source[0];
                if (!source) return false;
                let isLogbook = false;
                let psp;
                let sourceType;
                // set psp and isLogbook
                let sParts = source.split('___');
                if (source === 'uiTag') {
                    sourceType = 'uiTag';
                }
                else if (sParts.length === 3) { // NOSONAR if is dataset name
                    isLogbook = sParts[1];
                    if (isLogbook === '1') psp = 'lbe';
                    else psp = configStore.pspParentNamesAbbrev[sParts[0]];
                    sourceType = 'dataset';
                } else if (source.indexOf('Affiliation') !== -1) { // if is sDs call and not a fields request
                    psp = 'aff';
                    sourceType = 'sds';
                } else if (source === 'sds_getAffAvailabilityWindows') { // if is sDs call and not a fields request
                    psp = 'aaw';
                    sourceType = 'sds';
                } else if (source === 'er_messageList') { // Recruiter Requets
                    psp = 'msg';
                    sourceType = 'sds';
                } else if (source.indexOf('sds_getProfileField') === 0) { // if is sDs call and not a fields request
                    psp = params.recordPsp || 'ae';
                    sourceType = 'sdsFields';
                } else if (source.indexOf('ec_getHistory') === 0) { // if is sDs call and not a fields request
                    psp = 'history';
                    sourceType = 'sds';
                } else if (source.indexOf('ec_getDatasetHistory') === 0) { // if is sDs call and not a fields request
                    psp = 'history';
                    sourceType = 'sds';
                } else if (source.indexOf('ec_getGrid') === 0) { // if is sDs call and not a fields request
                    psp = params.gridPsp;
                    sourceType = 'sds';
                } else if (source.indexOf('ec_getRoles') === 0) { // if is sDs call and not a fields request
                    psp = 'userRole';
                    sourceType = 'sds';
                } else if (source.indexOf('getCstProperty') !== -1) {
                    psp = 'cstProperty';
                    sourceType = 'sds';
                } else if (source.indexOf('er_getPlacedSlotList') !== -1 || source.indexOf('er_getReferredSlotList') !== -1) {
                    psp = 'sch';
                    sourceType = 'sds';
                } else if (source.indexOf('er_getReferredOppList') !== -1 || source.indexOf('er_getPlacedOppList') !== -1) {
                    psp = 'opp';
                    sourceType = 'sds';
                } else if (source.indexOf('sds_get') === 0) { // if is sDs call and not a fields request
                    psp = source.split('sds_get')[1].substring(0, 3).toLowerCase(); // NOSONAR psp for sds_getVolAffiliations is vol
                    if (psp === 'aes') psp = 'sch'; // matches sds_getAeSlotsList
                    if (psp === 'ass') psp = 'assignment'; // matches sds_getAssignmentList
                    sourceType = 'sds';
                } else if (source.indexOf('configStore') === 0) {
                    sourceType = 'configStore';
                } else { // if it is a fields sDs call
                    sourceType = 'fields';
                    let tmpMatches = /er_(.*)Field/g.exec(source);
                    if (typeof tmpMatches?.[1] !== 'undefined') {
                        psp = tmpMatches[1];
                    }
                }

                let sKey = library.getUniqueID('nn');
                // It returns records from the recordStore whenever recordIds are added or removed
                let subscribers = {};
                if (subscriber) subscribers[subscriber] = 'subscribed';
                // Create path to subscription details
                this.subscriptionSummaries[sKey] = {
                    rowCount: 0,
                    summary: {}
                };
                this.subscriptions[sKey] = {
                    key: sKey,
                    payload,
                    params, // from payload
                    parentRecords, // from payload
                    source, // from payload
                    addParams, // from payload
                    sourceType,
                    sourceLogic,
                    psp,
                    tagName,
                    tagLogic,
                    tagParent,
                    subscribers,
                    isLogbook,
                    sDsParams,
                    recordIds: [], // Records are added in the subscribe action after server response
                    updateFrequency: typeof updateFrequency === 'number' ? updateFrequency : 360, // in seconds
                    updateFrequencyOnError: 60, // in seconds
                    isErrorCaptured: false,
                    lastRequestTime: false,
                    performance: {
                        serverResponseTime: {
                            history: [],
                            best: null,
                            worst: null
                        },
                        recordStorePopulationTime: {
                            history: [],
                            best: null,
                            worst: null
                        }
                    },
                    initialLoading: true,
                    loading: false
                };
                // TODO add updateFrequency Logic eg: { logic: "record.VOL_AGE < 18", frequency: 10 } // Sets frequency to 10 seconds
                existingSub = this.subscriptions[sKey];
            }
            return existingSub;
        },
        markErrorCaptured(sKey, failedRequest) {
            if (this.subscriptions?.[sKey]?.isErrorCaptured && failedRequest) {
                // error was captured in previous request and current request also has error
                let prevUpdateFrequencyOnError = parseInt(this.subscriptions[sKey].updateFrequencyOnError, 10);
                if (prevUpdateFrequencyOnError + 60 < 300) { // waiting should be less than 5 min
                    this.subscriptions[sKey].updateFrequencyOnError = prevUpdateFrequencyOnError + 60; // increase update frequency for 1 minute
                }
            }
            if (!failedRequest && this?.subscriptions?.[sKey]) {
                this.subscriptions[sKey].updateFrequencyOnError = 60; // return to initial value
            }
            if (this?.subscriptions?.[sKey]) this.subscriptions[sKey].isErrorCaptured = failedRequest;
        },
        subscribe(payload, subscriber, isUpdateRequest, existingKey, tagParent, fromSubDelay, force) {
            // Takes in sDs request params and generate a new, or find existing subscription
            // Sends request
            // Returns subscription Object
            // Subscriptions include all details to rerun the request including:
            // Perspective (psp), isLogbook, sDs request parameters (params)
            // Also includes settings for updateFrequency, and last request timestamp
            // subscriber is control name, cId of component, or other systematic ID in the code
            // Start the subscriber polling
            this.startSubscriberPolling();

            function formatFieldsParams(fields) {
                return Object.values(fields)
                    .map((field) => field.split(':::')[1] || field)
                    .filter(Boolean);
            }

            // Process source and params
            let subscriptionDetails = this.getSubscriptionDetails(payload, existingKey, subscriber, tagParent);
            if (subscriptionDetails.existing && !isUpdateRequest && !fromSubDelay && !force) return subscriptionDetails;
            if (!fromSubDelay) {
                // template can have multiple recordStore.records.vol[volId].VOL_FIRST_NAME
                // we do not need to call sDs each time, we should wait until all fields will be populated to the subscription
                // following code move the subscribe function to the timeout
                clearTimeout(this.subscriptions[subscriptionDetails.key].subscribeTimeout);
                this.subscriptions[subscriptionDetails.key].subscribeTimeout = setTimeout(() => {
                    this.subscribe(subscriptionDetails.payload, subscriber, isUpdateRequest, existingKey, tagParent, true, force);
                }, 500);
                return subscriptionDetails;
            }
            if (typeof this?.subscriptions?.[subscriptionDetails.key]?.subscribeTimeout !== 'undefined') {
                delete this.subscriptions[subscriptionDetails.key].subscribeTimeout;
            }
            let { psp, isLogbook, source, params, parentRecords, addParams, sourceLogic } = subscriptionDetails;
            // Tells the server the date to use for data cutoff
            let modifiedAfter = (!force && isUpdateRequest && subscriptionDetails.lastRequestTime) || false;
            // Send Request and process response
            let start = performance.now();
            let lastRequestTime = Math.floor(Date.now() / 1000); // NOSONAR;
            if (subscriptionDetails.sourceType === 'uiTag') {
                // Do nothing with uiTagged subs
            }
            else if (subscriptionDetails.sourceType === 'dataset') {
                // Prepare request params
                let requestOptions = {
                    sDsFunction: 'sds_getDataset',
                    subscriptionKey: subscriptionDetails.key,
                    subscriptionReloading: !!modifiedAfter
                };
                if (typeof subscriptionDetails.sDsParams !== 'undefined') {
                    requestOptions["sDsParams"] = subscriptionDetails.sDsParams;
                }
                // TODO Merge addParams from payload
                let otherAddParams = { useUdfCode: true, idAsKey: true, resetKey: false }; // TODO: What is resetKey?
                addParams = { ...addParams, ...otherAddParams };
                if (modifiedAfter) {
                    addParams.modifiedAfter = modifiedAfter;
                    if (parentRecords) addParams.currentSubscriptionRecords = subscriptionDetails.recordIds;
                }
                // Send the request
                if (!parentRecords) parentRecords = [];
                this.subscriptions[subscriptionDetails.key].loading = true;
                sDs.post(requestOptions, source, psp, isLogbook, parentRecords, addParams, sourceLogic)
                    .then((response) => {
                        if (typeof this.subscriptions[subscriptionDetails.key] !== 'undefined') {
                            locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                            if (response && response.status === 'success' && response.data) {
                                if (response.data.rowCount) {
                                    this.subscriptionSummaries[subscriptionDetails.key].rowCount = response.data.rowCount;
                                }
                                if (response.data.summary) {
                                    this.subscriptionSummaries[subscriptionDetails.key].summary = response.data.summary;
                                }
                                this.updateSubscriptionRecords(response.data.records, subscriptionDetails.key, modifiedAfter);
                            }
                            this.markErrorCaptured(subscriptionDetails.key, !(response && response.status === 'success'));
                            this.subscriptions[subscriptionDetails.key].initialLoading = false;
                            this.subscriptions[subscriptionDetails.key].loading = false;
                            this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                        }
                    });
            }
            else if (subscriptionDetails.sourceType === 'sds') {
                // Parse the object of params into an array of params based on order required by sDs
                // TODO replace this with more robust mapping (only works for sds_getAffiliationList
                let sdsOption = configStore.getsDsList().find((option) => option.value === source);
                // let sourceParamMap = {
                // sds_getAffiliationList
                //  volId: 0,
                //  orgId: 1,
                //  fields: 2,
                //  affType: 3,
                // ec_getGrid
                //  gridPsp: 0,
                //  gridIds: 1,
                //  gridFields: 2,
                // ec_getHistory
                //  recordId: 0,
                //  type: 1,
                //  filter: 2,
                // er_getCstProperty, ec_getCstProperty
                //  cstProperty: 0,
                //  fromDevDash: 1,
                //  useLoggedUserInstance: 2
                // };
                // TODO Note: This order might be different for different sDs calls
                // TODO Rework this formattedPayload method
                let formattedPayload = ["", "", []]; // These are default values
                let paramList = Object.keys(params);
                let requestParamsLen = 0;

                // Set default param values (We need to have all params filled up to the highest param sent)
                paramList.forEach((pKey) => {
                    if (pKey === 'source') return; // Source is handled separately
                    let paramIndex = sdsOption.params[pKey] && sdsOption.params[pKey].paramIndex;
                    if (typeof paramIndex === 'undefined' && psp === 'cstProperty' && pKey === 'cstProperty') {
                        paramIndex = 0;
                    }
                    if (typeof paramIndex !== 'undefined') {
                        if (requestParamsLen < paramIndex) {
                            // Set the length of payload array to highest params index
                            requestParamsLen = paramIndex;
                        }
                    }
                });
                if (modifiedAfter && sdsOption.addParamsIndex && sdsOption.addParamsIndex > requestParamsLen) {
                    requestParamsLen = sdsOption.addParamsIndex;
                }
                formattedPayload.length = requestParamsLen + 1; // Adjust for 0 index
                formattedPayload.fill(false);

                // Overwrite default params
                paramList.forEach((pKey) => {
                    if (pKey === 'source') return; // Source is handled separately
                    let paramIndex = sdsOption.params[pKey] && sdsOption.params[pKey].paramIndex;
                    if (typeof paramIndex === 'undefined' && psp === 'cstProperty' && pKey === 'cstProperty') {
                        paramIndex = 0;
                    }
                    if (typeof paramIndex !== 'undefined') {
                        if (typeof params[pKey] !== 'undefined') {
                            // Overwrite default values
                            if (sdsOption.params[pKey]?.isFieldsParam) formattedPayload[paramIndex] = formatFieldsParams(params[pKey]);
                            else formattedPayload[paramIndex] = params[pKey];
                        }
                    }
                });

                let requestOptions = {
                    sDsFunction: source,
                    subscriptionKey: subscriptionDetails.key,
                    subscriptionReloading: !!modifiedAfter
                };
                if (typeof subscriptionDetails.sDsParams !== 'undefined') {
                    requestOptions["sDsParams"] = subscriptionDetails.sDsParams;
                }
                // TODO make this more robust, allow it to be configured from the sources options
                let fieldMap = (field) => (field.split(':::').length === 2 ? field.split(':::')[1] : field);

                if (source === 'ec_getGrid' && Array.isArray(formattedPayload[2])) {
                    formattedPayload[2] = formattedPayload[2].map(fieldMap);
                    if (modifiedAfter) {
                        formattedPayload[6] = {
                            modifiedAfter: modifiedAfter,
                            currentSubscriptionRecords: subscriptionDetails.recordIds
                        };
                    }
                }
                if (source === 'sds_getAssignmentList' || source === 'sds_getAffiliationList') {
                    // Convert to psp based structure if not simple fields array
                    let fParamId = source === 'sds_getAssignmentList' ? 1 : 2;
                    if (Array.isArray(formattedPayload[fParamId]) && formattedPayload[fParamId].length && formattedPayload[fParamId][0].indexOf(':::') !== -1) {
                        let fieldsObj = {};
                        formattedPayload[fParamId].forEach((field) => {
                            let pspId = configStore.pspIds[field.split(':::')[0]];
                            let fieldName = field.split(':::')[1];
                            if (!fieldsObj[pspId]) fieldsObj[pspId] = [];
                            fieldsObj[pspId].push(fieldName);
                        });
                        formattedPayload[fParamId] = fieldsObj;
                    }
                }
                if (source === 'sds_getAeSlotsList') {
                    formattedPayload[2] = library.parseRelativeDate(formattedPayload[2]);
                    formattedPayload[3] = library.parseRelativeDate(formattedPayload[3]);
                }
                if (source === 'sds_getAeSlotsList' && Array.isArray(formattedPayload[1])) {
                    formattedPayload[1] = formattedPayload[1].map(fieldMap);
                    if (isUpdateRequest) {
                        formattedPayload[8] = {
                            modifiedAfter: modifiedAfter,
                            currentSubscriptionRecords: subscriptionDetails.recordIds
                        };
                    }
                }
                if (source === 'er_getPlacedSlotList' && isUpdateRequest) {
                    let volId = formattedPayload[5]; // NOSONAR
                    let currentSubscriptionRecords = {};
                    currentSubscriptionRecords[volId] = subscriptionDetails.recordIds;
                    formattedPayload[7] = { // NOSONAR
                        modifiedAfter: modifiedAfter,
                        currentSubscriptionRecords
                    };
                }
                if (source === 'ec_getHistory') {
                    this.subscriptions[subscriptionDetails.key].idAsKey = true;
                    formattedPayload[3] = { 'idAsKey': true };
                }
                this.subscriptions[subscriptionDetails.key].loading = true;
                sDs.post(requestOptions, ...formattedPayload).then((response) => {
                    locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                    if (response && response.status === 'success' && response.data) {
                        let data = response.data;
                        if (psp === 'cstProperty') {
                            if (!library.isJson(response.data) && typeof response.data?.[params.cstProperty] !== 'undefined') {
                                data = response.data[params.cstProperty];
                            }
                            data = JSON.parse(data);
                            if (params.subKey) data = data[params.subKey];
                        }
                        // Connect sub-records inside affiliation record
                        // TODO have each sub-record be connected to a recordStore proxy of the true record
                        if (source === 'sds_getAffiliationList' && data.ORGANIZATION) {
                            let orgData = library.cloneObj(data.ORGANIZATION);
                            delete data.ORGANIZATION;
                            for (let affId in data) {
                                if (affId === 'length') continue;
                                if (data[affId].AFF_RECORDS[3].length) {
                                    if (!data[affId].AFFILIATED_ORGS) data[affId].AFFILIATED_ORGS = {};
                                    // eslint-disable-next-line no-loop-func
                                    data[affId].AFF_RECORDS[3].forEach((aOrgId) => {
                                        data[affId].AFFILIATED_ORGS[aOrgId] = orgData[aOrgId];
                                    });
                                }
                                data[affId].DETAIL_RECORD = {};
                                let id1 = Array.isArray(data[affId].DETAIL_RECORD_ID) && data[affId].DETAIL_RECORD_ID[0];
                                if (id1) data[affId].DETAIL_RECORD = orgData[id1];
                            }
                        }
                        if (source === 'sds_getAssignmentList' && data.ASSIGNMENTS) {
                            let perspectives = Object.keys(data.RECORDS);
                            perspectives.forEach((pspId) => {
                                let pspAbbrev = configStore.pspParentNamesAbbrev[pspId];
                                if (pspAbbrev) {
                                    // Add records to but don't update subscription
                                    this.addRecordsToStore(data.RECORDS[pspId], pspAbbrev, subscriptionDetails);
                                }
                            });
                            let subRecords = library.cloneObj(data.RECORDS);
                            data = data.ASSIGNMENTS; // Map assignments as the main output of the subscription
                            // Merge sub records into assignment records
                            for (let assignmentId in data) {
                                if (assignmentId === 'length') continue;
                                let assignment = data[assignmentId];
                                assignment.subRecords = {
                                    aff: {},
                                    org: {},
                                    vol: false,
                                    opp: false
                                };
                                if (subRecords[32]) {
                                    for (let tierId in assignment.LINKED_AFFILIATIONS) {
                                        if (tierId === 'length') continue;
                                        let affId = assignment.LINKED_AFFILIATIONS[tierId];
                                        assignment.subRecords.aff[tierId] = subRecords[32][affId];
                                    }
                                }
                                for (let rKey in assignment.LINKED_RECORDS) {
                                    if (rKey === 'length') continue;
                                    let recordId = assignment.LINKED_RECORDS[rKey]; // recordId can be vol, opp, or various orgs
                                    if (!recordId) continue;
                                    if (rKey === 'VOL' && subRecords[1]) assignment.subRecords.vol = subRecords[1][recordId];
                                    else if (rKey === 'OPP' && subRecords[2]) assignment.subRecords.opp = subRecords[2][recordId];
                                    else if (rKey === 'ORGANIZATIONS' && subRecords[3]) {
                                        // TODO complete this later
                                    } else if (subRecords[3]) assignment.subRecords.org[rKey] = subRecords[3][recordId]; // All Tiered Orgs
                                }
                            }
                        }
                        this.updateSubscriptionRecords(data, subscriptionDetails.key, modifiedAfter);
                    }
                    this.markErrorCaptured(subscriptionDetails.key, !(response && response.status === 'success'));
                    if (typeof this?.subscriptions?.[subscriptionDetails.key] !== 'undefined') {
                        this.subscriptions[subscriptionDetails.key].initialLoading = false;
                        this.subscriptions[subscriptionDetails.key].loading = false;
                        this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                    }
                });
            }
            else if (subscriptionDetails.sourceType === 'sdsFields') {
                // handle sDs calls to specific records
                let postRequest;
                let fields = params.fields;
                let recordId = params.recordId;
                let requestOptions = {
                    sDsFunction: 'sds_getProfileField',
                    subscriptionKey: subscriptionDetails.key,
                    subscriptionReloading: !!modifiedAfter
                };
                if (typeof subscriptionDetails.sDsParams !== 'undefined') {
                    requestOptions["sDsParams"] = subscriptionDetails.sDsParams;
                }
                // eslint-disable-next-line no-case-declarations
                let tmpParams = { ...{ modifiedAfter: modifiedAfter }, ...params.params };
                if (psp && recordId && fields) {
                    this.subscriptions[subscriptionDetails.key].loading = true;
                    postRequest = sDs.post(requestOptions, psp, recordId, fields, tmpParams);
                    if (!postRequest) return false; // No Key is Produced
                    postRequest.then((response) => {
                        locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                        if (response && response.status === 'success' && response.data) {
                            this.updateSubscriptionRecords(response.data, subscriptionDetails.key, modifiedAfter);
                        }
                        this.markErrorCaptured(subscriptionDetails.key, !(response && response.status === 'success'));
                        if (typeof this?.subscriptions?.[subscriptionDetails.key] !== 'undefined') {
                            this.subscriptions[subscriptionDetails.key].initialLoading = false;
                            this.subscriptions[subscriptionDetails.key].loading = false;
                            this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                        }
                    });
                }
            }
            else {
                // handle sDs calls to specific records
                let prefix = psp === 'user' ? 'ec_' : 'er_';
                if (psp === 'sch') psp = 'slot';
                let fields = params.fields;
                let recordId = params.recordId;
                let requestOptions = {
                    sDsFunction: prefix + psp + 'Field',
                    subscriptionKey: subscriptionDetails.key,
                    subscriptionReloading: !!modifiedAfter
                };
                if (typeof subscriptionDetails.sDsParams !== 'undefined') {
                    requestOptions["sDsParams"] = subscriptionDetails.sDsParams;
                }
                this.subscriptions[subscriptionDetails.key].loading = true;
                let setHash = fields.join('');
                let uniqueSets = {};
                uniqueSets[setHash] = {
                    fields: fields,
                    recordId: recordId
                };
                // TODO Ensure this only runs once and then use standard param fields
                let tmpRecordFields = this.subscriptions[subscriptionDetails.key].recordFields;
                if (tmpRecordFields) {
                    // get only necessary fields for records
                    let setHash = tmpRecordFields.fields.join('');
                    if (typeof uniqueSets[setHash] === 'undefined') {
                        uniqueSets[setHash] = tmpRecordFields;
                    }
                    // let tmpRecordFields = library.cloneObj(this.subscriptions[subscriptionDetails.key].recordFields);
                    // uniqueSets = {};
                    // for (let key in tmpRecordFields) {
                    //     if (tmpRecordFields.hasOwnProperty(key)) {
                    //         let setHash = tmpRecordFields[key].join('');
                    //         if (typeof uniqueSets[setHash] === 'undefined') {
                    //             uniqueSets[setHash] = {
                    //                 fields: tmpRecordFields[key],
                    //                 recordId: [key]
                    //             };
                    //         } else {
                    //             if (uniqueSets[setHash].recordId.indexOf(key) === -1) uniqueSets[setHash].recordId.push(key);
                    //         }
                    //     }
                    // }
                }
                for (let i in uniqueSets) {
                    let postRequest;
                    let tmpFields = uniqueSets[i].fields;
                    let tmpRecordId = uniqueSets[i].recordId;
                    /* if (!modifiedAfter && this.records[psp][recordId]) {
                        // do not load field if it already loading in any subscription
                        let tmpFields = [];
                        for (let i in fields) {
                            if (typeof this.records[psp][recordId][fields[i]] === 'undefined' || this.records[psp][recordId][fields[i]] === null) {
                                tmpFields.push(fields[i]);
                            }
                        }
                        this.subscriptions[subscriptionDetails.key].loading = false;
                        if (!tmpFields.length) return subscriptionDetails;
                        fields = library.cloneObj(tmpFields);
                    } */

                    switch (psp) {
                        case 'user':
                            postRequest = sDs.post(requestOptions, tmpFields, tmpRecordId, false, modifiedAfter);
                            break;
                        case 'vol':
                        case 'opp':
                        case 'org':
                        case 'mem':
                        case 'slot':
                        case 'sch':
                        case 'acc':
                            if (psp === 'slot') {
                                psp = 'sch';
                            }
                            requestOptions.sDsFunction = 'sds_getProfileField';
                            postRequest = sDs.post(requestOptions, psp, tmpRecordId, tmpFields, false, false, false, modifiedAfter);
                            break;
                        case 'ae':
                        case 'aff':
                            requestOptions.sDsFunction = 'sds_getProfileField';
                            // eslint-disable-next-line no-case-declarations
                            let tmpParams = { ...{ modifiedAfter: modifiedAfter }, ...params.params };
                            postRequest = sDs.post(requestOptions, psp, tmpRecordId, tmpFields, tmpParams);
                            break;
                    }
                    if (!postRequest) return false; // No Key is Produced
                    postRequest.then((response) => {
                        locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                        if (response && response.status === 'success' && response.data) {
                            this.updateSubscriptionRecords(response.data, subscriptionDetails.key, modifiedAfter);
                        }
                        this.markErrorCaptured(subscriptionDetails.key, !(response && response.status === 'success'));
                        if (typeof this?.subscriptions?.[subscriptionDetails.key] !== 'undefined') {
                            this.subscriptions[subscriptionDetails.key].initialLoading = false;
                            this.subscriptions[subscriptionDetails.key].loading = false;
                            this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                        }
                    });
                }
            }
            return subscriptionDetails;
        },
        unsubscribe(existingKey, subscriber) {
            // This does not remove records from the store
            // subscriber is control name, cId of component, or other systematic ID in the code
            // providing a subscriber will remove that subscriber from one (with existingKey) or all subscriptions
            // if a subscription has no more subscribers it will be removed from the subscriptions list and no send updates
            // unsubscribe() is executed from componentMixins in unmounted() lifecycle hook
            let subscriptionKeys = [];
            // Remove a single subscription from all components, or remove one subscription from one component
            if (existingKey) subscriptionKeys.push(existingKey);
            // Allow component to remove all of it's subscriptions
            else if (subscriber) {
                let subKeys = Object.keys(this.subscriptions);
                subKeys.forEach((sKey) => {
                    if (this.subscriptions[sKey].subscribers[subscriber]) subscriptionKeys.push(sKey);
                });
            }
            subscriptionKeys.forEach((sKey) => {
                delete this.subscriptions[sKey].subscribers[subscriber];
                if (!Object.keys(this.subscriptions[sKey].subscribers).length || !subscriber) {
                    delete this.subscriptions[sKey];
                    // setting rsLastModified to 0 deletes uiTagged data
                    // this.rsLastModified[sKey] = -1;
                }
            });
        },
        renewSubscriptions(options) {
            // Go through all subscriptions
            // Find any that need to be updated according to their on specific time
            let currentTimestamp = Math.floor(Date.now() / 1000); // NOSONAR
            let reloadIDs;
            let reloadPsp;
            let force = false;
            if (typeof options === 'object') {
                if (typeof options.psp === 'string') reloadPsp = options.psp;
                if (Array.isArray(options.ids)) {
                    reloadIDs = library.cloneObj(options.ids);
                    for (let i in reloadIDs) {
                        reloadIDs[i] = parseInt(reloadIDs[i], 10);
                    }
                }
                force = options?.force;
            }
            Object.keys(this.subscriptions).forEach((key) => {
                let subscription = this.subscriptions[key];
                if (!force && typeof reloadPsp === 'string' && subscription.psp.toLowerCase() === reloadPsp.toLowerCase()) {
                    // reload if ids are not passed or ids are passed and some of them in subscription.recordIds
                    force = Array.isArray(reloadIDs) ? reloadIDs.some((tmpId) => subscription.recordIds.includes(tmpId)) : true;
                }
                if (subscription.updateFrequency === 0 && !force) return;
                let updateFrequency = subscription.isErrorCaptured ? subscription.updateFrequencyOnError : subscription.updateFrequency;
                if (subscription.lastRequestTime && (force || (subscription.lastRequestTime + updateFrequency < currentTimestamp))) {
                    // send the request
                    this.subscribe(false, false, true, subscription.key, false, false, force);
                }
            });
            locals.timeSinceLastRefresh = 0;
        },
        setModified(mRecords, forceReload) {
            // TODO delete setModified function
            // Example input
            // {
            //     opp: [12345],
            //     vol: [11111]
            // }
            // let subsToUpdate = [];
            // for (let psp in mRecords) {
            //     if (!Array.isArray(mRecords[psp])) continue;
            //     for (let index in mRecords[psp]) { // [11111]
            //         let mRecordId = mRecords[psp][index];
            //         let recordInStore = this.records[psp][mRecordId];
            //         let subKeys = [];
            //         if (recordInStore.inSubscription) {
            //             subKeys = Object.keys(recordInStore.inSubscription);
            //             subsToUpdate = [...subsToUpdate, ...subKeys];
            //         }
            //         if (recordInStore.uiDelete) {
            //             subKeys.forEach((subscriptionKey) => {
            //                 let subRecords = this.subscriptions[subscriptionKey]?.recordIds;
            //                 if (!subRecords) return;
            //                 let subRecordIndex = Array.isArray(subRecords) && subRecords.indexOf(mRecordId);
            //                 subRecords.splice(subRecordIndex, 1);
            //                 delete this.records.subscription[subscriptionKey].records[mRecordId];
            //             });
            //             delete this.records[psp][mRecordId];
            //         }
            //     }
            // }
            // subsToUpdate.forEach((subscriptionKey) => {
            //     // this.rsLastModified[subscriptionKey] = new Date().getTime();
            //     if (forceReload) this.subscribe(false, false, true, subscriptionKey);
            // });
        },
        // setRecords(iRecords, psp) {
        //     let recordKeys = Object.keys(iRecords);
        //     if (!recordKeys.length) return;
        //     recordKeys.forEach((recId) => {
        //         autosubscribe[psp][recId] = false;
        //         // eslint-disable-next-line no-use-before-define
        //         let rawRecord = toRaw(this.records[psp][recId]);
        //         // TODO Do I need to do strict records here?
        //         // let rawStrictRecord = toRaw(this.records.strict[psp][recId]);
        //         library.mergeObj(rawRecord, iRecords[recId]);
        //         this.records[psp][recId] = rawRecord;
        //         autosubscribe[psp][recId] = true;
        //     });
        // },
        addRecordsToStore(records, psp, subscriptionDetails = {}) {
            let recordKeys = [];
            let recordIds = [];
            if (typeof records !== 'object') return { records: [], recordIds };
            let { params, key: subscriptionKey } = subscriptionDetails;
            if (!Array.isArray(records)) {
                let recordArr = [];
                Object.keys(records).forEach((rKey) => {
                    recordKeys.push(rKey); // Might be recordId eg: for ec_getHistory
                    recordArr.push(records[rKey]);
                });
                records = recordArr;
            }
            let deletedRecords = [];
            records.forEach((record, index) => {
                // TODO add/call formatters here
                if (!record.uiRecordId) record.uiRecordId = record.rowID || record.ID || record.id || record.VOLUNTEER_ID || record.OPPORTUNITY_ID || record.ORGANIZATION_ID || record.ROLE_ID || recordKeys[index] || index;
                let recordId = record.uiRecordId;
                if (typeof recordId === 'string') recordId = Number(recordId);
                if (isNaN(recordId)) return;
                recordIds.push(recordId);
                if (typeof record === 'object' && record.deleted === true) {
                    deletedRecords.push(recordId);
                    return;
                }
                // Add parent record ID to history
                if (psp === 'history') {
                    record.recordId = params.recordId;
                }
                if (psp === 'cstProperty') {
                    if (!params) return;
                    // TODO move these next few lines into the proxy?
                    if (!this.records[psp][params.cstProperty]) this.records[psp][params.cstProperty] = {};
                    if (params.subKey) {
                        if (!this.records[psp][params.cstProperty][params.subKey]) {
                            this.records[psp][params.cstProperty][params.subKey] = {};
                        }
                        this.records[psp][params.cstProperty][params.subKey][recordId] = record;
                    } else {
                        this.records[psp][params.cstProperty][recordId] = record;
                    }
                }
                else if (typeof autosubscribe[psp]?.[recordId] === 'undefined') {
                    if (typeof this.records[psp] === 'undefined') this.records[psp] = {};
                    if (typeof this.records[psp][recordId] === 'undefined') this.records[psp][recordId] = {};
                    this.records[psp][recordId] = record;
                    // TODO do this in the proxy for only certain record types
                    this.records['strict'][psp][recordId] = record;
                    autosubscribe[psp][recordId] = true;
                } else {
                    // library.mergeObj(this.records[psp][recordId], record);
                    // we should rewrite data retrieved from server, not merge it
                    autosubscribe[psp][recordId] = false;
                    // let storeRecord = this.records[psp][recordId];
                    // let strictRecord = this.records.strict[psp][recordId];
                    /* let rawRecord;
                    let rawStrictRecord;
                    for (let fName in record) {
                        if (record[fName] !== currentHandlerStore?.[psp]?.[recordId]?.[fName]) {
                            if (!rawRecord) {
                                rawRecord = toRaw(this.records[psp][recordId]);
                                rawStrictRecord = toRaw(this.records.strict[psp][recordId]);
                            }
                            rawRecord[fName] = record[fName];
                            rawStrictRecord[fName] = record[fName];
                        }
                    }
                    // Merge back in the raw record
                    if (rawRecord) {
                        this.records[psp][recordId] = rawRecord;
                        this.records.strict[psp][recordId] = rawStrictRecord;
                    } */
                    for (let fName in record) {
                        // handlerStore.records[psp][recordId][fName] = record[fName];
                        this.records[psp][recordId][fName] = record[fName];
                        this.records['strict'][psp][recordId][fName] = record[fName];
                    }
                    autosubscribe[psp][recordId] = true;
                }
                if (subscriptionKey) {
                    if (!inSubscription[psp][recordId]) inSubscription[psp][recordId] = {};
                    inSubscription[psp][recordId][subscriptionKey] = true;
                }
            });
            if (deletedRecords.length) {
                let tmpDeletedRecords = {};
                tmpDeletedRecords[psp] = deletedRecords;
                this.deleteRecord(tmpDeletedRecords);
            }
            return { records, recordIds };
        },
        updateSubscriptionRecords(records, subscriptionKey, modifiedAfter) {
            // Use this to put a record into the store
            // This function returns an array of IDs for added records
            let start = performance.now();
            let subscription = this.subscriptions[subscriptionKey];
            if (!subscription) return;
            if (!records || typeof records !== 'object' || (modifiedAfter && library.objIsEmpty(records))) {
                locals.updatePerformance(subscription, 'recordStorePopulationTime', performance.now() - start);
                return;
            }
            let { psp, parentRecords } = subscription;
            // Add the records to the store
            let recordIds = [];
            ({ records, recordIds } = this.addRecordsToStore(records, psp, subscription));
            // Convert key/value records to array of records for other adding functions
            // if (!Array.isArray(records)) {
            //     let recordKeys = Object.keys(records);
            //     let recordArr = [];
            //     recordKeys.forEach((rKey) => {
            //         // records[rKey].uiRecordKey = rKey; // Might be recordId eg: for ec_getHistory
            //         recordArr.push(records[rKey]);
            //     });
            //     records = recordArr;
            // }
            // if (!modifiedAfter) subscription.recordIds = []; // Reset records on fresh requests
            // let { psp, recordIds, parentRecords, params, storeOnlyReturnedRecords } = subscription;
            // if (storeOnlyReturnedRecords) recordIds = [];
            let parentVols;
            let parentOpps;
            if (parentRecords) {
                parentVols = Array.isArray(parentRecords[1]) && parentRecords[1].length && parentRecords[1];
                parentOpps = Array.isArray(parentRecords[2]) && parentRecords[2].length && parentRecords[1];
            }
            let propInRecords = (prop) => records.length
                && typeof records[0] === 'object'
                && typeof records[0][prop] !== 'undefined';
            if (psp === 'aff') this.updateAffiliatedRecords(records);
            if (psp === 'opp' && parentVols && propInRecords('AFFILIATION_STATUS')) {
                this.updatePlaceReferRecords(records, psp, parentRecords, subscription);
            }
            if (psp === 'vol' && parentOpps && propInRecords('AFFILIATION_STATUS')) {
                this.updatePlaceReferRecords(records, psp, parentRecords, subscription);
            }
            if (psp === 'history') {
                this.updateHistoryRecords(records, subscription);
            }
            if (psp === 'sch') this.updateShiftRecords(records, subscription);
            if (psp === 'lbe') this.updateLbeRecords(records);
            // Update uiTagged Records
            this.subscriptions[subscriptionKey].recordIds = recordIds;
            // this.rsLastModified[subscriptionKey] = new Date().getTime();
            locals.updatePerformance(subscription, 'recordStorePopulationTime', performance.now() - start);
        },
        deleteRecord(records) {
            for (let psp in records) {
                if (psp === 'length') continue;
                let pspSubscriptions = inSubscription[psp];
                records[psp].forEach((recordId) => {
                    for (let subId in pspSubscriptions[recordId]) {
                        let tmpRecordIds = library.cloneObj(this.subscriptions?.[subId]?.recordIds);
                        if (!Array.isArray(tmpRecordIds)) return;
                        let recordIndex = tmpRecordIds.indexOf(recordId);
                        // TODO combine recordId replacement when multiple records are deleted and they are all in the same subscription
                        if (recordIndex > -1) tmpRecordIds.splice(recordIndex, 1);
                        this.subscriptions[subId].recordIds = tmpRecordIds;
                    }
                });
            }
        },
        updateHistoryRecords(records, subscription) {
            // TODO link history records to vols, opps, orgs, aff
        },
        updateAffiliatedRecords(records) {
            // TODO simplify this into a single function
            let volRecords = {};
            let orgRecords = {};
            records.forEach((record) => {
                let volId = record.VOLUNTEER_ID;
                let orgId = record.ORGANIZATION_ID;
                if (volId) {
                    let vol = volRecords[volId];
                    if (!vol) {
                        vol = { affIds: {}, affiliations: {}, VOLUNTEER_ID: volId };
                        if (record.AFF_VOLUNTEER) vol.fullName = record.AFF_VOLUNTEER;
                        volRecords[volId] = vol;
                    }
                    volRecords[volId].affIds[record.uiRecordId] = record.ORGANIZATION_ID;
                    record.linked_vol = this.records.vol[volId]; // This record gets processed through 'link' kType
                }
                if (orgId) {
                    let org = orgRecords[orgId];
                    if (!org) {
                        org = { affIds: {}, affiliations: {}, ORGANIZATION_ID: orgId };
                        if (record.AFF_ORGANIZATION) org.title = record.AFF_ORGANIZATION;
                        orgRecords[orgId] = org;
                    }
                    orgRecords[orgId].affIds[record.uiRecordId] = record.VOLUNTEER_ID;
                    record.linked_org = this.records.org[orgId]; // This record gets processed through 'link' kType
                }
            });
            this.addRecordsToStore(volRecords, 'vol');
            this.addRecordsToStore(orgRecords, 'org');
        },
        updatePlaceReferRecords(records, psp, parentRecords, subscription) {
            // TODO simplify this into a single function
            let volRecords = {};
            let oppRecords = {};
            records.forEach((record) => {
                let volId;
                let oppId;
                if (psp === 'vol') {
                    volId = record.VOLUNTEER_ID || record.rowID || record.ID;
                    oppId = parentRecords[2][0];
                }
                if (psp === 'opp') {
                    volId = parentRecords[1][0];
                    oppId = record.uiRecordId || record.OPPORTUNITY_ID || record.rowID || record.ID;
                }
                if (!volId || !oppId) return;
                // When setting referrals and placements, we must first reset all recordStore links inside sub records
                // Delete existing referrals and placements that match this volunteer and opportunity
                // Delete temp records
                // TODO: get rid of this by returning the refer/place ID in the sDs response
                let tempId = oppId + '00001';
                delete this.records.opp[oppId].referredIds[tempId];
                delete this.records.opp[oppId].placedIds[tempId];
                delete this.records.vol[volId].referredIds[tempId];
                delete this.records.vol[volId].placedIds[tempId];
                delete this.records.placements[tempId];
                delete this.records.referrals[tempId];
                let matchingReferrals = Object.values(this.records.opp[oppId].referrals)
                    .filter((rRecord) => typeof rRecord === 'object' && rRecord.VOL_ID === volId);
                let matchingPlacements = Object.values(this.records.opp[oppId].placements)
                    .filter((pRecord) => typeof pRecord === 'object' && pRecord.VOL_ID === volId);
                matchingReferrals.forEach((referral) => {
                    let rId = referral.uiRecordId;
                    delete this.records.opp[oppId].referredIds[rId];
                    delete this.records.vol[volId].referredIds[rId];
                    delete this.records.referrals[rId];
                });
                matchingPlacements.forEach((placement) => {
                    let pId = placement.uiRecordId;
                    delete this.records.opp[oppId].placedIds[pId];
                    delete this.records.vol[volId].placedIds[pId];
                    delete this.records.placements[pId];
                });
                let vol = volRecords[volId];
                if (!vol) {
                    vol = { placedIds: {}, referredIds: {}, placements: {}, referrals: {}, VOLUNTEER_ID: volId };
                    // Set some default values from the record
                    volRecords[volId] = vol;
                }
                let opp = oppRecords[oppId];
                if (!opp) {
                    opp = { placedIds: {}, referredIds: {}, placements: {}, referrals: {}, OPPORTUNITY_ID: oppId };
                    // Set some default values from the record
                    oppRecords[oppId] = opp;
                }
                if (record.AFFILIATION_STATUS === null) {
                    // Remove the record from the subscription if no longer affiliated
                    let recordId = record.uiRecordId || record.rowID || record.VOLUNTEER_ID || record.OPPORTUNITY_ID || record.ID;
                    let idIndex = subscription.recordIds.indexOf(recordId);
                    subscription.recordIds.splice(idIndex, 1);
                }
                else if (Array.isArray(record.AFFILIATION_STATUS)) {
                    record.AFFILIATION_STATUS.forEach((rpRecord) => {
                        rpRecord.OPP_ID = oppId;
                        rpRecord.VOL_ID = volId;
                        delete rpRecord.RECORD_ID;
                        let id = rpRecord.ID;
                        if (rpRecord.STATUS === 'referred') {
                            this.records.referrals[id] = rpRecord;
                            oppRecords[oppId].referredIds[id] = volId;
                            volRecords[volId].referredIds[id] = oppId;
                        }
                        if (rpRecord.STATUS === 'placed') {
                            this.records.placements[id] = rpRecord;
                            volRecords[volId].placedIds[id] = oppId;
                            oppRecords[oppId].placedIds[id] = volId;
                        }
                    });
                }
            });
            this.addRecordsToStore(volRecords, 'vol');
            this.addRecordsToStore(oppRecords, 'opp');
        },
        updateShiftRecords(records, subscription) {
            let volRecords = {};
            let oppRecords = {};
            let orgRecords = {};
            // TODO simplify this into a single function
            records.forEach((record) => {
                let volId = record.VOLUNTEER_ID || subscription?.params?.volId;
                let oppId = record.OPPORTUNITY_ID;
                let orgId = record.ORGANIZATION_ID;
                if (volId) {
                    let vol = volRecords[volId];
                    if (!vol) {
                        vol = { schIds: {}, shifts: {}, VOLUNTEER_ID: volId };
                        if (record.VOLUNTEER_NAME) vol.fullName = record.VOLUNTEER_NAME;
                        volRecords[volId] = vol;
                    }
                    volRecords[volId].schIds[record.uiRecordId] = true;
                    record.linked_vol = this.records.vol[volId]; // This record gets processed through 'link' kType
                }
                if (oppId) {
                    let opp = oppRecords[oppId];
                    if (!opp) {
                        opp = { schIds: {}, shifts: {}, OPPORTUNITY_ID: oppId };
                        if (record.OPP_TITLE) opp.OPP_TITLE = record.OPP_TITLE;
                        oppRecords[oppId] = opp;
                    }
                    oppRecords[oppId].schIds[record.uiRecordId] = true;
                    record.linked_opp = this.records.opp[oppId]; // This record gets processed through 'link' kType
                }
                if (record.ORGANIZATION_ID) {
                    let org = orgRecords[record.ORGANIZATION_ID];
                    if (!org) {
                        org = { schIds: {}, shifts: {}, ORGANIZATION_ID: orgId };
                        if (record.ORG_TITLE) org.ORG_TITLE = record.ORG_TITLE;
                        orgRecords[volId] = org;
                    }
                    orgRecords[volId].schIds[record.uiRecordId] = true;
                    record.linked_org = this.records.org[orgId]; // This record gets processed through 'link' kType
                }
            });
            this.addRecordsToStore(volRecords, 'vol');
            this.addRecordsToStore(oppRecords, 'opp');
            this.addRecordsToStore(orgRecords, 'org');
        },
        updateLbeRecords(records) {
            // TODO simplify this into a single function
            // Create a copy of records.vol // opp // org
            let volRecords = {};
            let oppRecords = {};
            let orgRecords = {};
            // Build our partial vol, org, opp records using lbe data on the records
            records.forEach((record) => {
                let volId = record.VOLUNTEER_ID;
                let oppId = record.OPPORTUNITY_ID;
                let orgId = record.ORGANIZATION_ID;
                if (volId) {
                    let vol = volRecords[volId];
                    if (!vol) {
                        vol = { lbeIds: {}, lbes: {}, VOLUNTEER_ID: volId };
                        if (record.VOLUNTEER_NAME) vol.fullName = record.VOLUNTEER_NAME;
                        volRecords[volId] = vol;
                    }
                    volRecords[volId].lbeIds[record.uiRecordId] = true;
                    record.linked_vol = this.records.vol[volId]; // This record gets processed through 'link' kType
                }
                if (oppId) {
                    let opp = oppRecords[oppId];
                    if (!opp) {
                        opp = { lbeIds: {}, lbes: {}, OPPORTUNITY_ID: oppId };
                        if (record.LOGBOOK_OPP_TITLE) opp.OPP_TITLE = record.LOGBOOK_OPP_TITLE;
                        oppRecords[oppId] = opp;
                    }
                    oppRecords[oppId].lbeIds[record.uiRecordId] = true;
                    record.linked_opp = this.records.opp[oppId]; // This record gets processed through 'link' kType
                }
                if (orgId) {
                    let org = orgRecords[orgId];
                    if (!org) {
                        org = { lbeIds: {}, lbes: {}, ORGANIZATION_ID: orgId };
                        if (record.LOGBOOK_ORG_TITLE) org.ORG_TITLE = record.LOGBOOK_ORG_TITLE;
                        orgRecords[orgId] = org;
                    }
                    orgRecords[orgId].lbeIds[record.uiRecordId] = true;
                    record.linked_org = this.records.org[orgId]; // This record gets processed through 'link' kType
                }
            });
            this.addRecordsToStore(volRecords, 'vol');
            this.addRecordsToStore(oppRecords, 'opp');
            this.addRecordsToStore(orgRecords, 'org');
        }
        // TODO: remove unused items from the recordStore, when?
    }
});
export { recordStore };
